import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from 'reactstrap';
import { memo, useEffect, useState } from 'react';
import { TextField } from '@material-ui/core';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import api from 'services/api';
import { enumStatus, iMaterial } from 'models/MaterialAcessorio';
import { downloadFileFromUrl } from 'utils/download';
import { FaDownload } from 'react-icons/fa';

type ModalFiltroProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  changeStatus: (a: number, b: string) => void;
  orcamentoMaterialId?: number;
  propostaVersaoId?: number;
  orcamentoId?: number;
};

const ModalMaterialAcessorio: React.FC<ModalFiltroProps> = ({
  orcamentoMaterialId,
  propostaVersaoId,
  orcamentoId,
  isOpen,
  setIsOpen,
  changeStatus,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [descricao, setDescricao] = useState('');
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [fileToSend, setFileToSend] = useState('');

  const [dados, setDados] = useState<iMaterial>();

  async function buscarDadosMaterial(id: number) {
    const response = await api.get(
      `/material-acessorio/orcamento-material-id/${id}`,
    );

    const convertido = response.data as iMaterial;
    setDados(convertido);
    setDescricao(convertido?.descricao);
    setIsBlocked(!['R', 'P'].includes(convertido?.status ?? 'P'));
  }

  useEffect(() => {
    if (orcamentoMaterialId) buscarDadosMaterial(orcamentoMaterialId);
  }, [orcamentoMaterialId]);

  async function uploadFile(referenciaId: number) {
    const toastIdAnexo = toast.loading('Salvando Anexos');

    try {
      const formData = new FormData();
      formData.append('file', fileToSend);

      await api.post(`/material-acessorio/${referenciaId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error: any) {
      const mensagem = `Não foi possível prosseguir com o upload do arquivo. ${error?.response?.data?.message}`;

      throw mensagem;
    } finally {
      setFileToSend('');

      const fileInput = document.getElementById(
        'fileUpload',
      ) as HTMLInputElement;

      if (fileInput) {
        fileInput.value = '';
      }

      toast.dismiss(toastIdAnexo);
    }
  }

  async function handleSalvar() {
    if (!descricao) {
      toast.error('Descrição precisa ser informada');
      return;
    }

    const resultado = await Swal.fire({
      title: `Deseja prosseguir com a solicitação de aprovação para este item acessório? Se uma aprovação for necessária, o fluxo será disparado.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Sim, prosseguir`,
      confirmButtonColor: '#d33',
      cancelButtonText: `Fechar`,
    });

    if (resultado.isConfirmed) {
      setIsLoading(true);
      const toastIdCriacao = toast.loading('Criando registro');
      const response = await api.post('/material-acessorio', {
        orcamentoMaterialId,
        propostaVersaoId,
        orcamentoId,
        descricao,
      });

      const { status, mensagem, referenciaId } = response.data as {
        status: string;
        mensagem: string;
        referenciaId: number;
      };

      toast.dismiss(toastIdCriacao);

      if (fileToSend) await uploadFile(referenciaId);

      if (status === 'A') {
        const toastIdAlerta = toast.loading('Enviando Alerta');

        await api.post(
          `/material-acessorio/${referenciaId}/disparar-mensagem-aprovacao`,
          {},
        );

        toast.dismiss(toastIdAlerta);
      }

      toast.success('Processo concluído');

      // Tudo pronto
      if (orcamentoMaterialId) changeStatus(orcamentoMaterialId, status);
      else if (propostaVersaoId) changeStatus(propostaVersaoId, status);

      setIsOpen(false);
      setIsLoading(false);
    }
  }

  const readUploadFile = async (e: any) => {
    e.preventDefault();
    if (e.target.files) {
      setFileToSend(e.target.files[0]);
    }
  };

  return (
    <Modal isOpen={isOpen} size="lg" className="modal-dialog-centered">
      <ModalHeader toggle={() => setIsOpen(false)}>
        Solicitação de Aprovação [{enumStatus[String(dados?.status ?? 'P')]}]:
        <div>
          <small>
            {dados?.material.codigoSap} - {dados?.material.descricao}
          </small>
        </div>
        <div hidden={!isBlocked} className="mt-1">
          <small>
            {`Solicitado por: ${String(
              dados?.usuarioSolicitante.username,
            ).toUpperCase()}`}
          </small>
        </div>
        <div hidden={!isBlocked}>
          <small>{`Perfil Responsável: ${dados?.perfil.nome} | Status: ${
            enumStatus[String(dados?.status ?? 'P')]
          }`}</small>
        </div>
        <div hidden={!isBlocked}>
          {dados?.usuarioAprovacao
            ? `${enumStatus[String(dados?.status ?? 'P')]} por: ${String(
                dados.usuarioAprovacao.username,
              ).toUpperCase()}`
            : ``}
        </div>
      </ModalHeader>
      <ModalBody style={{ maxHeight: '65vh', overflowY: 'auto' }}>
        <Row className="mt-1">
          <Col sm="12">
            <TextField
              variant="outlined"
              label="Descrição da Solicitação"
              multiline
              rows={5}
              value={descricao ?? ''}
              onChange={event => setDescricao(event.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                style: {
                  minHeight: 120,
                  alignItems: 'flex-start',
                },
              }}
            />
          </Col>
        </Row>

        <Row className="mt-2">
          <Col sm="12">
            <strong>Anexos:</strong>
            <br />
            <input
              hidden={isBlocked}
              type="file"
              name="fileUpload"
              id="fileUpload"
              onChange={readUploadFile}
            />

            <Table>
              {dados?.MaterialAcessorioAnexos?.map(it => {
                return (
                  <>
                    <tr>
                      <td>{it.arquivo.nome} </td>
                      <td>
                        <Button
                          color="primary"
                          type="button"
                          size="sm"
                          className="float-right"
                          outline
                          disabled={isLoading}
                          title="Baixar arquivo"
                          onClick={() =>
                            downloadFileFromUrl(it.arquivo.url, it.arquivo.nome)
                          }
                        >
                          <FaDownload />
                        </Button>
                      </td>
                    </tr>
                  </>
                );
              })}
            </Table>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col sm="6">
            <Button
              className="w-100"
              color="white"
              type="button"
              onClick={() => setIsOpen(false)}
            >
              Cancelar
            </Button>
          </Col>

          <Col sm="6">
            <Button
              className="w-100"
              color="primary"
              type="button"
              disabled={isLoading || isBlocked}
              onClick={handleSalvar}
            >
              Salvar
            </Button>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default ModalMaterialAcessorio;
